import { useRef, useState } from "react";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, asError } from "@/api/client";
import { normalizeImageUrl } from "@/lib/imageUrl";

/**
 * Admin image uploader. Accepts a current value + onChange callback.
 * Lets the operator either drag-and-drop a file, pick one, or paste a URL.
 */
export default function ImageUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG/PNG/WEBP/GIF).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum 5 MB.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error(asError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const preview = value ? normalizeImageUrl(value) : null;

  return (
    <div className="space-y-2">
      <div
        data-testid="image-uploader-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !busy && fileRef.current?.click()}
        className={`relative cursor-pointer border-2 border-dashed rounded-md p-3 transition flex items-center gap-3 ${
          dragOver ? "border-[var(--green)] bg-green-50" : "border-[#e5dec9] hover:border-[var(--green)] bg-white"
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <div className="w-16 h-16 rounded-md overflow-hidden bg-[var(--bg-cream)] flex items-center justify-center shrink-0">
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={20} className="text-[var(--ink-3)]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[var(--ink)] flex items-center gap-2">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Drop an image or click to upload</>}
          </div>
          <div className="text-[11px] text-[var(--ink-3)] mt-0.5">JPG / PNG / WEBP / GIF · up to 5 MB</div>
        </div>
        {value && !busy && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="text-[var(--ink-3)] hover:text-red-600"
            aria-label="Remove image"
          >
            <X size={16} />
          </button>
        )}
      </div>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste an image URL"
        className="w-full bg-white border border-[#e5dec9] rounded-md px-3 py-2 text-xs focus:border-[var(--green)] outline-none"
      />
    </div>
  );
}
